import * as React from 'react'
import * as constants from '../static/constants'

const bodyClassCounts: { [className: string]: number } = {}

const addBodyClass = (className: string) => {
  bodyClassCounts[className] = (bodyClassCounts[className] || 0) + 1
  document.body.classList.add(className)
}

const removeBodyClass = (className: string) => {
  bodyClassCounts[className] = Math.max((bodyClassCounts[className] || 0) - 1, 0)

  if (bodyClassCounts[className] === 0) {
    delete bodyClassCounts[className]
    document.body.classList.remove(className)
  }
}

interface ILegacyMediaQueryList {
  addListener(listener: () => void): void
  removeListener(listener: () => void): void
}

const addMediaQueryChangeListener = (mediaQueryList: MediaQueryList, listener: () => void) => {
  if (typeof mediaQueryList.addEventListener === 'function') {
    mediaQueryList.addEventListener('change', listener)

    return () => {
      mediaQueryList.removeEventListener('change', listener)
    }
  }

  const legacyMediaQueryList = (mediaQueryList as unknown) as ILegacyMediaQueryList

  legacyMediaQueryList.addListener(listener)

  return () => {
    legacyMediaQueryList.removeListener(listener)
  }
}

export function SetDocumentTitle(title) {
  React.useEffect(() => {
    window.document.title = `${title} - ${constants.APP_NAME}`
  }, [])
}

export const useDebounce = <T>(value: T, delay: number) => {
  const [debouncedValue, setDebouncedValue] = React.useState<T>(value)

  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}

export const useBodyClassForMediaQuery = (
  className: string,
  mediaQuery: string,
  enabled: boolean = true
) => {
  React.useEffect(() => {
    if (!enabled) {
      return
    }

    const mediaQueryList = matchMedia(mediaQuery)
    let applied = false

    const updateBodyClass = () => {
      if (mediaQueryList.matches && !applied) {
        addBodyClass(className)
        applied = true
      } else if (!mediaQueryList.matches && applied) {
        removeBodyClass(className)
        applied = false
      }
    }

    updateBodyClass()
    const removeMediaQueryChangeListener = addMediaQueryChangeListener(
      mediaQueryList,
      updateBodyClass
    )

    return () => {
      removeMediaQueryChangeListener()
      if (applied) {
        removeBodyClass(className)
      }
    }
  }, [className, enabled, mediaQuery])
}
